import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Eye, Pencil, Download, Link2, QrCode, RefreshCw, Settings2, Trash2, ShieldCheck, FileUp, Sparkles, Loader2 } from "lucide-react";

const CATEGORIES = ["Office Memorandum", "Office Order", "Notification", "Circular", "Public Notice", "Direction", "Resolution", "Policy / Guideline", "Other Official Document"];
const STATUSES = ["ACTIVE", "SUPERSEDED", "WITHDRAWN", "ARCHIVED"];
const STATUS_CLS = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SUPERSEDED: "bg-amber-50 text-amber-700 border-amber-200",
  WITHDRAWN: "bg-red-50 text-red-600 border-red-200",
  ARCHIVED: "bg-slate-100 text-slate-500 border-slate-200",
};
const EMPTY = { title: "", category: "Office Memorandum", reference_no: "", doc_date: "", authority: "", description: "", keywords: "", status: "ACTIVE" };
function Field({ label, children }) { return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">{label}</label>{children}</div>; }

export default function OfficialDocsAdmin() {
  const [items, setItems] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [qrDoc, setQrDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [u, setU] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [edit, setEdit] = useState(null);
  const replaceRef = useRef(null);
  const setUf = (k, v) => setU((p) => ({ ...p, [k]: v }));
  const setEf = (k, v) => setEdit((p) => ({ ...p, [k]: v }));

  const load = () => api.get("/official-documents/manage/all").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const analyze = async (f) => {
    if (!f) return;
    setAnalyzing(true); setAiNote("");
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/official-documents/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const a = data.analysis || {};
      setU((p) => ({ ...p,
        title: a.title || p.title || f.name.replace(/\.[^.]+$/, ""),
        category: a.category || p.category,
        reference_no: a.reference_no || p.reference_no,
        doc_date: a.doc_date || p.doc_date,
        authority: a.authority || p.authority,
        description: a.description || p.description,
        keywords: Array.isArray(a.keywords) ? a.keywords.join(", ") : (a.keywords || p.keywords),
      }));
      if (a._ai_error) setAiNote(a._ai_error);
      else setAiNote(`AI auto-filled the details${data.ocr_used ? " (via OCR)" : ""}${a.needs_review ? " — some fields need review" : ""}. Please verify before uploading.`);
    } catch (e) { setAiNote(e.response?.data?.detail || "Auto-fill failed — please fill the details manually."); }
    finally { setAnalyzing(false); }
  };

  const doUpload = async () => {
    if (!file) return toast.error("Select a PDF file");
    if (!u.title.trim()) return toast.error("Document title is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(u).forEach(([k, v]) => fd.append(k, v));
      const { data } = await api.post("/official-documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded · ${data.doc_id} · QR-stamped ${data.pages} page(s)`);
      setUploadOpen(false); setU(EMPTY); setFile(null); setAiNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const openEdit = (d) => { setEdit({ ...d, keywords: (d.keywords || []).join(", ") }); setEditOpen(true); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const body = { ...edit, keywords: edit.keywords };
      await api.put(`/official-documents/${edit.id}`, body);
      toast.success("Saved"); setEditOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doReplace = async (d, f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("note", "");
      const { data } = await api.post(`/official-documents/replace/${d.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`New version ${data.doc_id} created; previous marked SUPERSEDED`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Replace failed"); }
    finally { setBusy(false); }
  };

  const del = async (d) => { if (!window.confirm(`Delete ${d.doc_id}? Its verification record will be removed.`)) return; await api.delete(`/official-documents/${d.id}`); toast.success("Deleted"); load(); };
  const copyLink = (d) => { navigator.clipboard.writeText(`${window.location.origin}/verify/${d.doc_id}`); toast.success("Verification link copied"); };

  return (
    <>
      <AdminHeader title="Official Documents" subtitle={`${items.length} document${items.length !== 1 ? "s" : ""}`} actions={
        <Button onClick={() => { setU(EMPTY); setFile(null); setAiNote(""); setUploadOpen(true); }} className="bg-npst-navy text-white" data-testid="upload-doc-btn"><Plus size={15} className="mr-1.5" /> Upload Document</Button>
      } />
      <AdminPage>
        <p className="text-sm text-slate-500 mb-4">Upload a PDF once — the system auto-generates a unique Document ID, SHA-256 fingerprint, verification URL and stamps a QR verification block on every page.</p>
        {items.length === 0 ? <p className="text-slate-400 text-sm">No documents yet.</p> : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  {["Document ID", "Title", "Category", "Ref No.", "Date", "Status", "DL", "Verif.", "Actions"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50" data-testid="admin-doc-row">
                    <td className="px-3 py-2.5 font-mono text-[11px] text-npst-navy whitespace-nowrap">{d.doc_id}</td>
                    <td className="px-3 py-2.5 max-w-[220px] truncate font-medium text-npst-ink" title={d.title}>{d.title}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">{d.category}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">{d.reference_no || "—"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">{d.doc_date || "—"}</td>
                    <td className="px-3 py-2.5"><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_CLS[d.status] || STATUS_CLS.ARCHIVED}`}>{d.status}</span></td>
                    <td className="px-3 py-2.5 text-slate-500 tabular-nums">{d.downloads || 0}</td>
                    <td className="px-3 py-2.5 text-slate-500 tabular-nums">{d.verifications || 0}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5 text-slate-400">
                        <a href={`${API}/official-documents/master/${d.id}`} target="_blank" rel="noopener noreferrer" title="View master" className="hover:text-npst-navy"><Eye size={15} /></a>
                        <a href={`${API}/official-documents/download/${d.doc_id}`} target="_blank" rel="noopener noreferrer" title="Verified PDF" className="hover:text-npst-navy"><Download size={15} /></a>
                        <button onClick={() => openEdit(d)} title="Edit / status / supersede" className="hover:text-npst-navy" data-testid="admin-doc-edit"><Settings2 size={15} /></button>
                        <button onClick={() => copyLink(d)} title="Copy verification link" className="hover:text-npst-navy"><Link2 size={15} /></button>
                        <button onClick={() => setQrDoc(d)} title="View QR" className="hover:text-npst-navy"><QrCode size={15} /></button>
                        <button onClick={() => replaceRef.current?.pick(d)} title="Replace / supersede" className="hover:text-amber-600"><RefreshCw size={15} /></button>
                        <button onClick={() => del(d)} title="Delete" className="hover:text-red-600"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminPage>

      {/* hidden replace picker */}
      <ReplacePicker ref={replaceRef} onFile={doReplace} />

      {/* UPLOAD DIALOG */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Upload Official Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="PDF File">
              <label className={`flex items-center gap-3 border-2 border-dashed rounded-md px-4 py-6 cursor-pointer transition-colors ${analyzing ? "border-npst-gold bg-amber-50/40" : "border-slate-300 hover:border-npst-gold"}`} data-testid="upload-filedrop">
                {analyzing ? <Loader2 size={20} className="text-npst-gold animate-spin" /> : <FileUp size={20} className="text-slate-400" />}
                <span className="text-sm text-slate-600">{analyzing ? "Reading document & auto-filling details…" : (file ? file.name : "Click to select a PDF (max 25MB) — details auto-fill with AI")}</span>
                <input type="file" accept="application/pdf" className="hidden" disabled={analyzing}
                  onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); analyze(f); }} data-testid="upload-file-input" />
              </label>
            </Field>
            {aiNote && (
              <div className={`flex items-start gap-2 text-xs rounded-md px-3 py-2.5 border ${aiNote.includes("auto-filled") ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="upload-ai-note">
                <Sparkles size={13} className="shrink-0 mt-0.5" /> <span>{aiNote}</span>
              </div>
            )}
            <Field label="Document Title"><Input value={u.title} onChange={(e) => setUf("title", e.target.value)} data-testid="upload-title" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category">
                <select value={u.category} onChange={(e) => setUf("category", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="upload-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={u.status} onChange={(e) => setUf("status", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="upload-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Reference / Document No."><Input value={u.reference_no} onChange={(e) => setUf("reference_no", e.target.value)} placeholder="NPST/OO/2026/001" data-testid="upload-ref" /></Field>
              <Field label="Date of Document"><Input type="date" value={u.doc_date} onChange={(e) => setUf("doc_date", e.target.value)} data-testid="upload-date" /></Field>
            </div>
            <Field label="Issuing Authority / Department"><Input value={u.authority} onChange={(e) => setUf("authority", e.target.value)} data-testid="upload-authority" /></Field>
            <Field label="Short Description"><Textarea value={u.description} onChange={(e) => setUf("description", e.target.value)} rows={2} data-testid="upload-description" /></Field>
            <Field label="Keywords / Tags (comma-separated)"><Input value={u.keywords} onChange={(e) => setUf("keywords", e.target.value)} placeholder="pension, revision, 2026" data-testid="upload-keywords" /></Field>
            <Button onClick={doUpload} disabled={busy} className="w-full bg-npst-navy text-white" data-testid="upload-submit"><ShieldCheck size={15} className="mr-1.5" /> {busy ? "Processing…" : "Upload & Generate Verification"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit Document Metadata</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="text-xs font-mono text-npst-navy">{edit.doc_id}</div>
              <Field label="Document Title"><Input value={edit.title} onChange={(e) => setEf("title", e.target.value)} data-testid="edit-title" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select value={edit.category} onChange={(e) => setEf("category", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select value={edit.status} onChange={(e) => setEf("status", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="edit-status">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Reference No."><Input value={edit.reference_no} onChange={(e) => setEf("reference_no", e.target.value)} /></Field>
                <Field label="Date"><Input type="date" value={edit.doc_date} onChange={(e) => setEf("doc_date", e.target.value)} /></Field>
              </div>
              <Field label="Issuing Authority"><Input value={edit.authority} onChange={(e) => setEf("authority", e.target.value)} /></Field>
              <Field label="Description"><Textarea value={edit.description} onChange={(e) => setEf("description", e.target.value)} rows={2} /></Field>
              <Field label="Keywords (comma-separated)"><Input value={edit.keywords} onChange={(e) => setEf("keywords", e.target.value)} /></Field>
              <Field label="Superseded By (Document ID of newer document — optional)"><Input value={edit.superseded_by || ""} onChange={(e) => setEf("superseded_by", e.target.value)} placeholder="NPST-XXXXXXXXXX" data-testid="edit-superseded-by" /></Field>
              <Field label="Supersede Note (optional)"><Input value={edit.supersede_note || ""} onChange={(e) => setEf("supersede_note", e.target.value)} placeholder="This document has been superseded by Office Order No. …" /></Field>
              <Button onClick={saveEdit} disabled={busy} className="w-full bg-npst-navy text-white" data-testid="edit-save">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR DIALOG */}
      <Dialog open={!!qrDoc} onOpenChange={(o) => !o && setQrDoc(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-xl">Verification QR</DialogTitle></DialogHeader>
          {qrDoc && (
            <div className="text-center space-y-3">
              <img src={`${API}/official-documents/qr/${qrDoc.id}`} alt="QR" className="w-56 h-56 mx-auto border border-slate-200 rounded-md" data-testid="qr-image" />
              <div className="font-mono text-sm text-npst-navy">{qrDoc.doc_id}</div>
              <div className="text-xs text-slate-500 break-all">{window.location.origin}/verify/{qrDoc.doc_id}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const ReplacePicker = React.forwardRef(({ onFile }, ref) => {
  const inputRef = useRef(null);
  const docRef = useRef(null);
  React.useImperativeHandle(ref, () => ({ pick: (d) => { docRef.current = d; inputRef.current?.click(); } }));
  return <input ref={inputRef} type="file" accept="application/pdf" className="hidden"
    onChange={(e) => { const f = e.target.files?.[0]; if (f && docRef.current) onFile(docRef.current, f); e.target.value = ""; }} />;
});
